import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import { createLocaleFormatters } from '../localization/formatters';
import { i18n } from '../localization/i18n';
import {
  getTranslationLanguage,
  type SupportedUiLocale,
  type TranslationLanguage,
} from '../localization/locales';
import { getBrowserLocale, resolveEffectiveUiLocale } from '../localization/localeResolver';
import { useUserPreferences } from './UserPreferencesContext';

interface LocalizationContextValue {
  effectiveLocale: SupportedUiLocale;
  formatters: ReturnType<typeof createLocaleFormatters>;
  isLocaleReady: boolean;
  translationLanguage: TranslationLanguage;
}

const LocalizationContext = createContext<LocalizationContextValue | undefined>(undefined);

export const LocalizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isInitialLoadComplete, preferences } = useUserPreferences();
  const effectiveLocale = resolveEffectiveUiLocale(preferences?.ui_locale ?? null, getBrowserLocale());
  const translationLanguage = getTranslationLanguage(effectiveLocale);
  const formatters = useMemo(
    () => createLocaleFormatters(effectiveLocale),
    [effectiveLocale],
  );
  const contextValue = useMemo<LocalizationContextValue>(() => ({
    effectiveLocale,
    formatters,
    isLocaleReady: isInitialLoadComplete,
    translationLanguage,
  }), [effectiveLocale, formatters, isInitialLoadComplete, translationLanguage]);

  useEffect(() => {
    document.documentElement.lang = effectiveLocale;

    let isCurrentLocale = true;
    void i18n.changeLanguage(translationLanguage).then(() => {
      if (isCurrentLocale) {
        document.title = i18n.t('common:documentTitle');
      }
    });

    return () => {
      isCurrentLocale = false;
    };
  }, [effectiveLocale, translationLanguage]);

  return (
    <I18nextProvider i18n={i18n}>
      <LocalizationContext.Provider value={contextValue}>
        {children}
      </LocalizationContext.Provider>
    </I18nextProvider>
  );
};

export const useLocalization = (): LocalizationContextValue => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within LocalizationProvider');
  }
  return context;
};

export const useFormatters = (): LocalizationContextValue['formatters'] => (
  useLocalization().formatters
);